import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { CommunicationServiceService } from '../../widgets/services/communication-service.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { HelperService } from '../../../services/helper.service';
import { AddMilestoneDialogComponent } from '../../widgets/add-milestone-dialog/add-milestone-dialog.component';
import { DialogService } from '../../../services/core/dialog.service';
import { AddTaskComponent } from '../../sale/task/add-task/add-task.component';
import { ModuleMilestoneSelectorComponent } from '../module-milestone-selector/module-milestone-selector.component';

@Component({
    selector: 'app-project-timeline-activity',
    templateUrl: './project-timeline-activity.component.html',
    styleUrls: ['./project-timeline-activity.component.scss'],
    standalone: false
})
export class ProjectTimelineActivityComponent implements OnInit, OnChanges, OnDestroy {
  @Input() moduleId: number = 19; // Default to Job module
  @Input() moduleName: any = "Project";
  @Input() recordId: number | null = null;
  /** Record display name (e.g. bid/job name) — shown as the reference label in the Add Task drawer. */
  @Input() recordName: string | null = null;
  @Input() timelinePhases: any[] = [];
  @Input() activityListsectionHeight: string = '500px';

  timelineLoading: boolean = false;
  activityLoading: boolean = false;
  recentActivities: any[] = [];
  activityFilter: 'tasks' | 'milestones' | 'all' = 'tasks';
  activityPage: number = 1;
  activityMeta: any = null;
  showLoadMoreActivities: boolean = false;
  private destroy$ = new Subject<void>();

  // Subject to push nearest milestone
  private nearestMilestoneSubject = new Subject<any>();
  // Observable that other components can subscribe to
  public nearestMilestone$ = this.nearestMilestoneSubject.asObservable();

  /** Task StatusSetting id flagged awarded_accepted=1 (the "completed" status). null = none configured. */
  completedTaskStatusId: number | null = null;
  /** Per-task in-flight guard so the tick can't be double-clicked. */
  taskCompleting: Record<string | number, boolean> = {};
  /** Per-milestone in-flight guard so the marker click can't be double-fired. */
  milestoneStatusUpdating: Record<string | number, boolean> = {};

  readonly milestoneCreatePermission = 'MILESTONE-CREATE';
  /** Needed to auto-advance a milestone's status when its tasks are completed. */
  readonly milestoneStatusPermission = 'MILESTONE-UPDATE-MILESTONE-STATUS';

  constructor(
    private http: HttpClient,
    private communicationService: CommunicationServiceService,
    private toaster: ToasterService,
    private dialog: MatDialog,
    private helperService: HelperService,
    private dialogService: DialogService
  ) { }

  /** Whether the current user can add a milestone (drives the "Add Milestone" button). */
  get canAddMilestone(): boolean {
    return this.helperService.isPermissionAvailable(this.milestoneCreatePermission);
  }

  /** Whether the current user can change a milestone's status (drives the clickable marker). */
  get canChangeMilestoneStatus(): boolean {
    return this.helperService.isPermissionAvailable(this.milestoneStatusPermission);
  }

  /**
   * Click the pending (empty-circle) timeline marker to start that milestone — moves it to
   * "in_progress" so it surfaces in the milestone phase card. Only acts on a pending milestone
   * and only with the status permission; reuses PUT module-data-milestones/{id}/status.
   */
  onMilestoneMarkerClick(phase: any, event?: Event): void {
    if (event) { event.stopPropagation(); }
    if (!phase || !phase.id) { return; }
    if (!this.canChangeMilestoneStatus) { return; }
    const raw = (phase.rawStatus || '').toString().toLowerCase();
    if (raw !== 'pending' && raw !== '') { return; }  // only promote a pending milestone
    if (this.milestoneStatusUpdating[phase.id]) { return; }

    this.milestoneStatusUpdating[phase.id] = true;
    // Reflect locally straight away (marker + phase card react on the coming refresh).
    phase.status = 'in_progress';
    phase.rawStatus = 'in_progress';
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}module-data-milestones/${phase.id}/status`;
    this.http.put<any>(apiUrl, { status: 'in_progress' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.milestoneStatusUpdating[phase.id] = false;
          this.toaster.showSuccessMessage('Milestone marked as in progress');
          this.communicationService.setMilestoneRefresh(true);
        },
        error: () => {
          this.milestoneStatusUpdating[phase.id] = false;
          this.toaster.showErrorMessage('Could not update the milestone status.');
          this.communicationService.setMilestoneRefresh(true); // re-sync from server
        }
      });
  }

  /** Open the shared Add-Milestone dialog for THIS bid/job; reload everything on success. */
  openAddMilestone(): void {
    if (!this.recordId || !this.canAddMilestone) { return; }
    const dialogRef = this.dialog.open(AddMilestoneDialogComponent, {
      width: '600px',
      panelClass: 'add-milestone-dialog-panel',
      data: { module_id: this.moduleId, reference_id: this.recordId }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload the timeline + every milestone bar (this card, phase card, tasks widget).
        this.communicationService.setMilestoneRefresh(true);
      }
    });
  }

  /**
   * Open the "Add Milestones" selector (attach milestone templates to THIS
   * bid/job). The modal filters templates to this module and, on save, attaches
   * the chosen milestones to the record and broadcasts setMilestoneRefresh(true)
   * — which this component already subscribes to, so the timeline reloads itself.
   */
  openAttachMilestone(): void {
    if (!this.recordId || !this.canAddMilestone) { return; }
    this.dialog.open(ModuleMilestoneSelectorComponent, {
      width: '700px',
      maxHeight: '80vh',
      panelClass: 'milestone-dialog-container',
      data: {
        dataId: this.recordId,
        moduleId: this.moduleId,
        moduleName: this.recordName || this.moduleName,
      },
    });
  }

  /**
   * Open the Add Task drawer for a specific milestone. The clicked milestone
   * (phase.id = module_data_milestones.id) is passed as `milestoneId` so the
   * drawer opens with it pre-attached; module/reference are locked to this
   * bid/job. On success the timeline reloads so the new task shows under it.
   */
  openAddTaskForMilestone(phase: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.recordId) { return; }
    const dialogRef = this.dialogService.onOpenFromRightDialog(AddTaskComponent, {
      moduleID: this.moduleId,
      referenceID: this.recordId,
      milestoneId: phase?.id,
      defaultProjectName: this.recordName || undefined,
    }, 1300);
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.communicationService.setMilestoneRefresh(true);
      }
    });
  }

  ngOnInit(): void {
    this.loadData();
    this.setupMilestoneRefreshSubscription();
    this.loadCompletedTaskStatus();
  }

  /** Find the task module's "completed" status (awarded_accepted=1) so the tick knows what to set. */
  private loadCompletedTaskStatus(): void {
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}statuses/task`;
    this.http.get<any>(apiUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const list = res?.data ?? res ?? [];
        const done = (Array.isArray(list) ? list : []).find((s: any) => Number(s?.awarded_accepted) === 1);
        this.completedTaskStatusId = done ? done.id : null;
      },
      error: () => { this.completedTaskStatusId = null; }
    });
  }

  /** Mark a milestone task complete — same API as the task detail (POST tasks/change-status/{id}),
   *  setting it to the awarded_accepted "completed" status; then refresh so the bars advance.
   *  If the parent milestone is still pending (not yet in progress / completed), completing a
   *  task kicks it into "in_progress" so it surfaces in the milestone phase card. */
  completeTask(task: any, phase?: any, event?: Event): void {
    if (event) { event.stopPropagation(); }
    if (!task || task.status === 'completed' || !this.completedTaskStatusId) { return; }
    if (this.taskCompleting[task.id]) { return; }

    this.taskCompleting[task.id] = true;
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}tasks/change-status/${task.id}`;
    this.http.post<any>(apiUrl, { status: this.completedTaskStatusId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          task.status = 'completed';                 // instant visual (green + line)
          this.taskCompleting[task.id] = false;
          this.toaster.showSuccessMessage('Task completed');
          // Advance the milestone status off the back of this completion:
          //  - last task done  -> mark the milestone completed
          //  - otherwise, a still-pending milestone -> in_progress (so it shows in the phase card)
          this.syncMilestoneStatusAfterTask(phase);
          // Reload the timeline + every milestone bar (this card, the phase card, the tasks widget).
          this.communicationService.setMilestoneRefresh(true);
        },
        error: () => {
          this.taskCompleting[task.id] = false;
          this.toaster.showErrorMessage('Could not complete the task.');
        }
      });
  }

  /**
   * After a task is completed, advance its milestone's status to match:
   *  - if EVERY task on the milestone is now completed -> mark the milestone "completed"
   *    (with completed_at, same payload as the milestone-timeline status dropdown);
   *  - otherwise, if the milestone is still "pending" -> move it to "in_progress" so it
   *    surfaces in the milestone phase card.
   * A milestone already "completed" is never downgraded. Uses the existing
   * PUT module-data-milestones/{id}/status endpoint. Failure is non-fatal: the task is
   * already completed and the timeline refresh still runs.
   */
  private syncMilestoneStatusAfterTask(phase: any): void {
    if (!phase || !phase.id) { return; }
    // Backend status endpoint is gated by MILESTONE-UPDATE-MILESTONE-STATUS; skip silently
    // (the task is already completed) when the user can't change milestone status.
    if (!this.helperService.isPermissionAvailable(this.milestoneStatusPermission)) { return; }
    const raw = (phase.rawStatus || '').toString().toLowerCase();
    if (raw === 'completed') { return; }

    // phase.tasks carries the (now-updated) per-task statuses; the just-completed task
    // was already flipped to 'completed' before this call.
    const tasks = Array.isArray(phase.tasks) ? phase.tasks : [];
    const allCompleted = tasks.length > 0 && tasks.every((t: any) => t.status === 'completed');

    let newStatus: 'in_progress' | 'completed';
    if (allCompleted) {
      newStatus = 'completed';
    } else if (raw === 'in_progress' || raw === 'in-progress') {
      return; // already in progress and not all done — nothing to change
    } else {
      newStatus = 'in_progress';
    }

    // Reflect it locally straight away so the phase card can pick it up on this refresh.
    phase.rawStatus = newStatus;
    const payload: any = { status: newStatus };
    if (newStatus === 'completed') {
      payload.completed_at = new Date().toISOString();
    }

    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}module-data-milestones/${phase.id}/status`;
    this.http.put<any>(apiUrl, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (newStatus === 'completed') {
            this.toaster.showSuccessMessage('Milestone completed');
          }
          // Reload again so the phase card reflects the server-confirmed status.
          this.communicationService.setMilestoneRefresh(true);
        },
        error: () => {
          this.toaster.showErrorMessage('Task completed, but could not update the milestone status.');
        }
      });
  }

  /**
   * Subscribe to milestone refresh events for auto-reload
   */
  private setupMilestoneRefreshSubscription(): void {
    this.communicationService.getMilestoneRefresh()
      .pipe(takeUntil(this.destroy$))
      .subscribe((refresh: boolean) => {
        if (refresh) {
          this.loadMilestoneTimeline();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when recordId changes
    if (changes['recordId'] && !changes['recordId'].firstChange) {
      this.loadData();
    }
  }

  private loadData(): void {
    if (this.recordId) {
      this.loadMilestoneTimeline();
      this.loadTaskActivities();
    }
  }

  /**
   * Load milestone data from API and transform to timeline format
   */
  loadMilestoneTimeline() {
    if (!this.recordId) {
      return;
    }

    this.timelineLoading = true;
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}module-data-milestones/${this.moduleId}/${this.recordId}`;


    this.http.get<any>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            this.timelinePhases = this.transformMilestonesToTimeline(response.data);

            // Find nearest milestone and push to observable
            const nearestMilestone = this.findNearestMilestone(response.data);
            if (nearestMilestone) {
              this.nearestMilestoneSubject.next(nearestMilestone);
            } else {
              // If no in_progress milestone found, emit null to clear the display
              this.nearestMilestoneSubject.next(null);
            }
          } else {
            this.timelinePhases = [];
          }
          this.timelineLoading = false;
        },
        error: (error) => {
          console.error('Error loading milestone timeline:', error);
          this.timelineLoading = false;
        }
      });
  }

  /**
   * Load task activities from NEW Task Activities API
   */
  loadTaskActivities(resetPage: boolean = true) {
    if (!this.recordId) {
      return;
    }

    if (resetPage) {
      this.activityPage = 1;
      this.recentActivities = [];
    }

    if (this.activityFilter === 'all') {
      this.loadAllActivities(resetPage);
      return;
    }

    this.activityLoading = true;
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}tasks/activities`;

    const params: any = {
      module_id: this.moduleId,
      data_id: this.recordId,
      milestone_only: this.activityFilter === 'milestones' ? 1 : 0,
      per_page: 10,
      page: this.activityPage,
      sort_order: 'desc'
    };

    this.http.get<any>(apiUrl, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {

          if (response.data && Array.isArray(response.data)) {
            const newActivities = this.transformActivitiesToDisplay(response.data);
            this.recentActivities = [...this.recentActivities, ...newActivities];
          }

          this.activityMeta = response.meta;
          this.updateLoadMoreOption();
          this.activityLoading = false;
        },
        error: (error) => {
          console.error('Error loading task activities:', error);
          this.activityLoading = false;
        }
      });
  }

  /**
   * Load all activities from general activity log API
   */
  loadAllActivities(resetPage: boolean = true) {
    if (!this.recordId) {
      return;
    }

    if (resetPage) {
      this.activityPage = 1;
      this.recentActivities = [];
    }

    this.activityLoading = true;
    const apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}activity/log`;

    const params: any = {
      module_id: this.moduleId,
      subject_id: this.recordId,
      sort_by: 'id',
      sort_order: 'DESC',
      per_page: 10,
      page: this.activityPage
    };

    this.http.get<any>(apiUrl, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {

          if (response.data && Array.isArray(response.data)) {
            const newActivities = this.transformAllActivitiesToDisplay(response.data);
            this.recentActivities = [...this.recentActivities, ...newActivities];
          }

          this.activityMeta = response.meta;
          this.updateLoadMoreOption();
          this.activityLoading = false;
        },
        error: (error) => {
          console.error('Error loading all activities:', error);
          this.activityLoading = false;
        }
      });
  }

  /**
   * Load more activities (next page)
   */
  loadMoreActivities() {
    if (this.activityLoading || !this.showLoadMoreActivities) {
      return;
    }

    this.activityPage += 1;
    this.loadTaskActivities(false);
  }

  /**
   * Update the load more button visibility based on pagination meta
   */
  updateLoadMoreOption() {
    if (!this.activityMeta) {
      this.showLoadMoreActivities = false;
      return;
    }

    this.showLoadMoreActivities = this.activityMeta.current_page < this.activityMeta.last_page;
  }

  /**
   * Transform task activities to display format
   */
  transformActivitiesToDisplay(activities: any[]): any[] {
    return activities.map(activity => {
      return {
        id: activity.Activity_id,
        title: activity.log_name || 'Task Activity',
        // TaskActivityController's perform_by override uses 'name', not 'full_name'
        // (unlike every other activity endpoint) -- check both.
        user: activity.perform_by?.full_name || activity.perform_by?.name || 'System',
        time: activity.created_at || '',
        description: activity.description || '',
        icon: this.getTaskActivityIcon(activity.event)
      };
    });
  }

  /**
   * Transform all activities to display format
   */
  transformAllActivitiesToDisplay(activities: any[]): any[] {
    return activities.map(activity => {
      return {
        id: activity.id,
        title: activity.log_name || 'Activity',
        user: activity.perform_by?.full_name || 'System',
        time: activity.created_at || '',
        description: activity.description || '',
        icon: this.getActivityIcon(activity.event || activity.log_name)
      };
    });
  }

  /**
   * Get task activity icon based on event type
   */
  getTaskActivityIcon(event: string): string {
    if (event === 'Created') return 'add_circle';
    if (event === 'Updated') return 'edit';
    if (event === 'Deleted') return 'delete';
    if (event === 'Status Changed') return 'swap_horiz';
    if (event === 'Viewed') return 'visibility';

    return 'task_alt';
  }

  /**
   * Get general activity icon based on event type or log name
   */
  getActivityIcon(eventOrLogName: string): string {
    if (!eventOrLogName) return 'notifications';

    const event = eventOrLogName.toLowerCase();

    if (event.includes('created')) return 'add_circle';
    if (event.includes('updated')) return 'edit';
    if (event.includes('deleted')) return 'delete';
    if (event.includes('status')) return 'swap_horiz';
    if (event.includes('viewed')) return 'visibility';
    if (event.includes('comment')) return 'comment';
    if (event.includes('note')) return 'note';
    if (event.includes('document') || event.includes('file') || event.includes('upload')) return 'attach_file';
    if (event.includes('milestone')) return 'flag';
    if (event.includes('assign')) return 'person_add';
    if (event.includes('job') || event.includes('project')) return 'work';

    return 'notifications';
  }

  /**
   * Format activity time to relative format
   */
  formatActivityTime(dateString: string): string {
    if (!dateString) return '';

    try {
      const activityDate = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - activityDate.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return activityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  }

  /**
   * Change activity filter and reload activities
   */
  setActivityFilter(filter: 'tasks' | 'milestones' | 'all') {
    this.activityFilter = filter;
    this.loadTaskActivities();
  }

  /**
   * Transform milestone data to timeline phase format
   */
  transformMilestonesToTimeline(milestones: any[]): any[] {
    return milestones.map((milestone: any) => {
      // Match the exact same logic as milestone-timeline component
      const tasks = milestone.tasks || [];
      const completedTasks = tasks.filter((task: any) =>
        task.is_completed_status === true ||
        task.completed_at != null
      );
      const totalTasks = tasks.length;
      const taskProgress = totalTasks > 0 ? Math.round((completedTasks.length / totalTasks) * 100) : 0;

      let status = 'pending';
      if (milestone.completed_at || milestone.status === 'completed') {
        status = 'completed';
      } else if (milestone.status === 'in_progress' || (taskProgress > 0 && taskProgress < 100)) {
        status = 'in_progress';
      }

      const endDate = milestone.due_date ? new Date(milestone.due_date) : new Date();
      const startDate = milestone.start_date
        ? new Date(milestone.start_date)
        : (milestone.created_at ? new Date(milestone.created_at) : null);

      let dateRange = '';
      if (startDate) {
        const startStr = this.formatDateShort(startDate);
        const endStr = this.formatDateShort(endDate);
        dateRange = `${startStr}-${endStr}`;
      } else {
        dateRange = this.formatDateShort(endDate);
      }

      return {
        id: milestone.id,
        name: milestone.milestone_name,
        status: status,
        // Raw backend milestone status (pending/in_progress/completed) — the phase card
        // selects by this, so completeTask() checks it before promoting to in_progress.
        rawStatus: milestone.status,
        progress: status === 'completed' ? 100 : (taskProgress || 0), // Ensure progress is always a number
        date: dateRange,
        startDate: milestone.start_date || milestone.created_at,
        endDate: milestone.due_date,
        // Tasks attached to this milestone, shown nested under it in the Timeline column.
        tasks: this.mapMilestoneTasks(milestone.tasks || [])
      };
    });
  }

  /**
   * Compact per-milestone task list for the Timeline column. Completion uses the same flag-based
   * rule as the milestone progress bar (is_completed_status / is_closed_status / completed_at) so
   * a renamed "completed" status still shows as done.
   */
  private mapMilestoneTasks(tasks: any[]): any[] {
    return (tasks || []).map((t: any) => {
      let status: 'completed' | 'in_progress' | 'pending' = 'pending';
      // "Completed" only means the status has the Awarded/Accepted/Completed flag (or an explicit
      // completed_at) — same rule as the milestone progress bar. A merely "closing" status
      // (is_closed_status, e.g. Cancelled/Declined) is NOT completed.
      if (t.is_completed_status === true || t.completed_at) {
        status = 'completed';
      } else if (t.is_default_status === true) {
        status = 'pending';
      } else {
        status = 'in_progress';
      }
      return {
        id: t.id,
        name: t.name || 'Untitled Task',
        status,
        statusName: t.status_name || '',
        date: t.due_date ? this.formatDateShort(t.due_date) : ''
      };
    });
  }

  /**
   * Format date to short format (e.g., "Oct 15")
   */
  formatDateShort(date: string | Date): string {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
    const day = dateObj.getDate();

    return `${month} ${day}`;
  }

  /**
   * Find the in_progress milestone with due_date nearest to current date
   * Rules:
   * 1. Only consider milestones with "in_progress" status
   * 2. If multiple in_progress milestones exist, select the one with due date closest to today
   * 3. If multiple have the same closest due date, return the first one
   */
  findNearestMilestone(milestones: any[]): any {
    if (!milestones || milestones.length === 0) {
      return null;
    }

    // Filter only in_progress milestones
    const inProgressMilestones = milestones.filter(milestone => {
      // Check various ways status might be stored
      const status = milestone.status || milestone.milestone_status || '';
      return status.toLowerCase() === 'in_progress' || status.toLowerCase() === 'in progress';
    });

    // If no in_progress milestones, return null
    if (inProgressMilestones.length === 0) {
      return null;
    }

    let nearestMilestone: any = null;

    // If only one in_progress milestone, use it
    if (inProgressMilestones.length === 1) {
      nearestMilestone = inProgressMilestones[0];
    } else {
      // Multiple in_progress milestones - find the one with due date nearest to current date
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Reset time to midnight

      let smallestDifference = Infinity;

      inProgressMilestones.forEach(milestone => {
        if (milestone.due_date) {
          const dueDate = new Date(milestone.due_date);
          dueDate.setHours(0, 0, 0, 0);

          // Calculate absolute difference in milliseconds
          const difference = Math.abs(dueDate.getTime() - currentDate.getTime());

          // If this milestone is closer to current date (or first one with same difference)
          if (difference < smallestDifference) {
            smallestDifference = difference;
            nearestMilestone = milestone;
          }
        } else {
          // If milestone has no due date, treat it as having infinite difference
          // It will only be selected if no other milestones have due dates
          if (nearestMilestone === null) {
            nearestMilestone = milestone;
          }
        }
      });

      // If no milestone with due_date was found, return the first in_progress milestone
      if (nearestMilestone === null && inProgressMilestones.length > 0) {
        nearestMilestone = inProgressMilestones[0];
      }
    }

    // Calculate and add progress to the milestone before returning
    if (nearestMilestone) {
      // Match the exact same logic as milestone-timeline component
      const tasks = nearestMilestone.tasks || [];

      if (tasks.length > 0) {
        // Match the exact filter logic from milestone-timeline component
        const completedTasks = tasks.filter((task: any) => {
          const isCompleted = task.is_completed_status === true ||
            task.completed_at != null;
          return isCompleted;
        });

        const totalTasks = tasks.length;
        const calculatedProgress = totalTasks > 0 ? Math.round((completedTasks.length / totalTasks) * 100) : 0;

        // Set the progress on the milestone object
        nearestMilestone.progress = calculatedProgress;
      } else {
        // No tasks, set progress to 0
        nearestMilestone.progress = 0;
      }

      // Also normalize the milestone name field
      if (!nearestMilestone.name && nearestMilestone.milestone_name) {
        nearestMilestone.name = nearestMilestone.milestone_name;
      }

      // Prepare additional milestones data (all in-progress except the nearest one)
      const additionalMilestones = inProgressMilestones
        .filter(m => m.id !== nearestMilestone.id)
        .map(milestone => {
          // Calculate progress for each additional milestone
          const tasks = milestone.tasks || [];
          let progress = 0;

          if (tasks.length > 0) {
            const completedTasks = tasks.filter((task: any) => {
              const isCompleted = task.is_completed_status === true ||
                task.completed_at != null;
              return isCompleted;
            });
            progress = Math.round((completedTasks.length / tasks.length) * 100);
          }

          return {
            id: milestone.id,
            name: milestone.name || milestone.milestone_name || '',
            status: milestone.status || 'in_progress',
            progress: progress,
            due_date: milestone.due_date,
            task_count: tasks.length,
            completed_task_count: tasks.filter((t: any) =>
              t.status === 'completed' ||
              t.status_name?.toLowerCase() === 'completed' ||
              t.completed_at !== null
            ).length
          };
        });

      // Create a clean milestone object with all necessary properties
      // This ensures the progress property is properly included
      const milestoneWithProgress = {
        ...nearestMilestone,
        progress: nearestMilestone.progress || 0,
        name: nearestMilestone.name || nearestMilestone.milestone_name,
        // Add count of additional in-progress milestones (excluding the current one)
        additionalCount: inProgressMilestones.length - 1,
        // Include the additional milestones data for tooltip display
        additionalMilestones: additionalMilestones
      };

      return milestoneWithProgress;
    }

    return nearestMilestone;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
